import React from 'react';
import { X, AlertCircle, CheckCircle, Info, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { clsx } from 'clsx';
import { useToastAutoDismiss } from '../../hooks/useToastAutoDismiss';

export interface ToastProps {
  message: string;
  type?: 'success' | 'error' | 'warning' | 'info';
  duration?: number;
  resetKey?: string;
  selectionVersion?: number;
  onClose?: () => void;
  className?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
}

const TOAST_LINK_CLASS =
  'whitespace-nowrap font-semibold underline underline-offset-2 hover:opacity-80';

function renderToastLink(text: string, href: string, key: string): React.ReactNode {
  if (href.startsWith('/')) {
    return (
      <Link key={key} to={href} className={TOAST_LINK_CLASS}>
        {text}
      </Link>
    );
  }

  const normalizedHref = href.toLowerCase();
  if (normalizedHref.startsWith('http://') || normalizedHref.startsWith('https://')) {
    return (
      <a key={key} href={href} className={TOAST_LINK_CLASS} target='_blank' rel='noreferrer'>
        {text}
      </a>
    );
  }

  return text;
}

function renderToastMessage(message: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  let cursor = 0;
  let textStart = 0;

  const pushText = (end: number) => {
    if (end > textStart) {
      parts.push(message.slice(textStart, end));
    }
  };

  while (cursor < message.length) {
    if (message.startsWith('**', cursor)) {
      const boldEnd = message.indexOf('**', cursor + 2);
      if (boldEnd > cursor + 2) {
        pushText(cursor);
        const boldText = message.slice(cursor + 2, boldEnd);
        const key = `bold-${cursor}`;
        parts.push(
          <strong key={key} className='font-semibold'>
            {boldText}
          </strong>,
        );
        cursor = boldEnd + 2;
        textStart = cursor;
        continue;
      }
      parts.push(message.slice(textStart));
      textStart = message.length;
      break;
    }

    if (message[cursor] === '[') {
      const labelEnd = message.indexOf(']', cursor + 1);
      if (labelEnd === -1) {
        parts.push(message.slice(textStart));
        textStart = message.length;
        break;
      }
      const hrefStart = labelEnd + 1;
      if (labelEnd > cursor + 1 && message[hrefStart] === '(') {
        const hrefEnd = message.indexOf(')', hrefStart + 1);
        if (hrefEnd > hrefStart + 1) {
          pushText(cursor);
          const linkText = message.slice(cursor + 1, labelEnd);
          const linkHref = message.slice(hrefStart + 1, hrefEnd);
          parts.push(renderToastLink(linkText, linkHref, `link-${cursor}`));
          cursor = hrefEnd + 1;
          textStart = cursor;
          continue;
        }
        parts.push(message.slice(textStart));
        textStart = message.length;
        break;
      }
      cursor = labelEnd + 1;
      continue;
    }

    cursor += 1;
  }

  if (textStart < message.length) {
    parts.push(message.slice(textStart));
  }

  return parts;
}

const Toast: React.FC<ToastProps> = ({
  message,
  type = 'info',
  duration = 5000,
  resetKey,
  selectionVersion,
  onClose,
  className,
  action,
}) => {
  const { toastRef, isVisible, handleClose, handleMouseEnter, handleMouseLeave, handleMouseUp } =
    useToastAutoDismiss({
      duration,
      resetKey,
      selectionVersion,
      onClose,
    });

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle className='w-6 h-6 text-green-600' />;
      case 'error':
        return <AlertCircle className='w-6 h-6 text-red-600' />;
      case 'warning':
        return <AlertTriangle className='w-6 h-6 text-yellow-600' />;
      case 'info':
      default:
        return <Info className='w-6 h-6 text-blue-600' />;
    }
  };

  const getBackgroundColor = () => {
    switch (type) {
      case 'success':
        return 'bg-green-50 border-green-200 text-green-800';
      case 'error':
        return 'bg-red-50 border-red-200 text-red-800';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      case 'info':
      default:
        return 'bg-blue-50 border-blue-200 text-blue-800';
    }
  };

  return (
    <div
      ref={toastRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseUp={handleMouseUp}
      className={clsx(
        'max-w-xl w-[32rem] transform transition-all duration-300 ease-in-out',
        isVisible ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0',
        className,
      )}
    >
      <div
        className={clsx(
          'flex items-start gap-4 p-5 rounded-lg border shadow-lg',
          getBackgroundColor(),
        )}
      >
        <div className='flex-shrink-0 mt-0.5'>{getIcon()}</div>
        <div className='flex-1 min-w-0'>
          <p className='text-sm sm:text-base font-medium leading-relaxed whitespace-pre-line'>
            {renderToastMessage(message)}
          </p>
          {action ? (
            <button
              type='button'
              onClick={action.onClick}
              className='mt-2 text-sm font-semibold underline underline-offset-2 opacity-90 hover:opacity-100'
            >
              {action.label}
            </button>
          ) : null}
        </div>
        <button
          onClick={handleClose}
          className='flex-shrink-0 ml-2 p-1.5 rounded-md hover:bg-black/10 transition-colors'
          aria-label='Close notification'
        >
          <X className='w-5 h-5' />
        </button>
      </div>
    </div>
  );
};

export default Toast;
